import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { companyAPI, reviewAPI, bookingAPI, userAPI, userCompanyAPI } from '../services/api';
import Layout from '../components/Layout';
import AvailableSlotsCalendar from '../components/AvailableSlotsCalendar';
import Icon from '../components/Icon';


function CompanyPublicProfilePage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState(null); // 'none', 'pending', 'connected', 'accepted'
  const [connectionId, setConnectionId] = useState(null); // For disconnect
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  // Booking form state
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedBikeId, setSelectedBikeId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchBikes();
    checkConnectionStatus();
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [cRes, rRes, tRes] = await Promise.all([
        companyAPI.getCompanyPublic(companyId),
        reviewAPI.listForCompany(companyId),
        bookingAPI.getCompanyServiceTemplates(companyId, true)
      ]);
      setCompany(cRes.data.company);
      setReviews(rRes.data.reviews || []);
      setTemplates(tRes.data.templates || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchBikes = async () => {
    try {
      const response = await userAPI.getMyBikes();
      setBikes(response.data.bikes || []);
    } catch (err) {
      console.log('Could not load bikes');
    }
  };

  const checkConnectionStatus = async () => {
    try {
      const [companiesRes, connectionsRes] = await Promise.all([
        userCompanyAPI.getAvailableCompanies(),
        userCompanyAPI.getUserConnections(),
      ]);
      const companies = companiesRes.data.companies || [];
      const thisCompany = companies.find(c => String(c.id) === String(companyId));
      if (thisCompany) {
        setConnectionStatus(thisCompany.connection_status || 'none');
      } else {
        setConnectionStatus('none');
      }
      // Get connection id for disconnect (when connected)
      const conn = (connectionsRes.data.connections || []).find(
        c => String(c.company_id) === String(companyId) && (c.status === 'accepted' || c.status === 'connected')
      );
      setConnectionId(conn?.id || null);
    } catch (err) {
      console.log('Could not check connection status');
      setConnectionStatus('none');
      setConnectionId(null);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError('');
      await userCompanyAPI.requestConnection(parseInt(companyId));
      setConnectionStatus('connected'); // Auto-accept for now
      setSuccess('Successfully connected to the service shop! You can now book appointments.');
      setTimeout(() => setSuccess(''), 3000);
      checkConnectionStatus(); // Refresh to get connection id
    } catch (err) {
      // If already connected, that's fine
      if (err.response?.data?.error?.includes('already')) {
        setConnectionStatus('connected');
        checkConnectionStatus();
      } else {
        setError(err.response?.data?.error || 'Failed to connect to service shop');
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!connectionId || !window.confirm('Disconnect from this service shop? They will no longer have access to your bike data.')) return;
    try {
      setDisconnecting(true);
      setError('');
      await userCompanyAPI.disconnectFromCompany(connectionId);
      setConnectionStatus('none');
      setConnectionId(null);
      setSuccess('Disconnected from service shop.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleStartBooking = async () => {
    // If not connected, connect first
    if (connectionStatus !== 'connected' && connectionStatus !== 'accepted') {
      await handleConnect();
    }
    // Navigate to booking wizard with pre-selected company
    navigate(`/book?companyId=${companyId}`);
  };

  const handleCalendarDateSelect = (dateStr) => {
    // Validate that bike and service are selected before allowing date selection
    if (!selectedBikeId) {
      setError('Please select a bike first');
      return;
    }
    if (!selectedTemplateId) {
      setError('Please select a service type first');
      return;
    }
    setError(''); // Clear any previous errors
    setSelectedDate(dateStr);
    setSelectedSlot(null);
  };

  const handleCalendarSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedBikeId || !selectedTemplateId || !selectedDate || !selectedSlot) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setBookingLoading(true);
      const bookingData = {
        bike_id: parseInt(selectedBikeId),
        service_template_id: parseInt(selectedTemplateId),
        booking_date: selectedDate,
        start_time: selectedSlot.start,
        technician_id: selectedSlot.technician_id || null,
        notes: notes || null
      };

      await bookingAPI.createBooking(parseInt(companyId), bookingData);
      setSuccess('Booking created successfully! You will receive a confirmation.');
      setSelectedSlot(null);
      setNotes('');
      setSelectedDate('');
      setSelectedTemplateId('');
      setShowBookingForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create booking');
    } finally {
      setBookingLoading(false);
    }
  };

  const renderMap = () => {
    if (!company || !company.latitude || !company.longitude) return null;
    const lat = company.latitude;
    const lng = company.longitude;
    const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01}%2C${lat-0.01}%2C${lng+0.01}%2C${lat+0.01}&layer=mapnik&marker=${lat}%2C${lng}`;
    return (
      <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
        <iframe
          title="map"
          width="100%"
          height="180"
          frameBorder="0"
          src={osmUrl}
          style={{ display: 'block' }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} pageTitle="Loading..." darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
        <div className="loading">Loading company...</div>
      </Layout>
    );
  }

  // Only show full page error if company failed to load (not for form validation errors)
  if (!company) {
    return (
      <Layout user={user} onLogout={onLogout} pageTitle="Error" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
        <div className="error">{error || 'Company not found'}</div>
      </Layout>
    );
  }

  return (
    <Layout 
      user={user} 
      onLogout={onLogout}
      pageTitle={company.name}
      pageSubtitle={company.address ? `${company.address.city || ''} ${company.address.street || ''}` : ''}
     darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
      {/* Contact & Rating Header */}
      <div style={{
        background: 'var(--bg-overlay-light)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          {/* Contact Info */}
          <div style={{ flex: '1', minWidth: '200px' }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#ffffff', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name="chat" color="primary" darkMode={darkMode} size={18} />
              Contact
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
              {company.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="email" color="secondary" darkMode={darkMode} size={14} />
                  {company.email}
                </div>
              )}
              {company.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="view" color="secondary" darkMode={darkMode} size={14} />
                  {company.phone}
                </div>
              )}
              {company.address && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="map" color="secondary" darkMode={darkMode} size={14} />
                  {company.address.street}, {company.address.city} {company.address.postcode}
                </div>
              )}
            </div>
          </div>

          {/* Compact Rating Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.08) 100%)',
            border: '1px solid rgba(251, 191, 36, 0.25)',
            borderRadius: '12px'
          }}>
            <Icon name="star" color="warning" darkMode={darkMode} size={20} />
            <div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#fbbf24', lineHeight: '1' }}>
                {(company.average_rating || 0).toFixed(1)}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                {company.review_count || 0} reviews
              </div>
            </div>
          </div>
        </div>

        {/* Map - smaller and optional */}
        {company.latitude && company.longitude && (
          <div style={{ marginTop: '16px' }}>
            {renderMap()}
          </div>
        )}
      </div>

      {/* Book Now Button */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button 
          onClick={showBookingForm ? () => setShowBookingForm(false) : handleStartBooking}
          className="btn btn-primary"
          disabled={connecting}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '16px', padding: '14px 28px' }}
        >
          {connecting ? (
            <>
              <Icon name="loading" color="primary" darkMode={darkMode} size={18} />
              Connecting...
            </>
          ) : showBookingForm ? (
            'Close'
          ) : (
            <>
              <Icon name="calendar" color="primary" darkMode={darkMode} size={18} />
              Book Appointment
            </>
          )}
        </button>
        
        {/* Connection status indicator */}
        {connectionStatus && connectionStatus !== 'none' && (
          <span style={{
            padding: '8px 16px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '500',
            background: connectionStatus === 'connected' || connectionStatus === 'accepted' 
              ? 'rgba(74, 140, 24, 0.25)' 
              : 'rgba(245, 158, 11, 0.15)',
            color: connectionStatus === 'connected' || connectionStatus === 'accepted'
              ? '#86efac'
              : '#f59e0b',
            border: `1px solid ${connectionStatus === 'connected' || connectionStatus === 'accepted' ? 'rgba(74, 140, 24, 0.4)' : 'rgba(245, 158, 11, 0.3)'}`,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {connectionStatus === 'connected' || connectionStatus === 'accepted' ? (
              <>
                <Icon name="success" color="success" darkMode={darkMode} size={14} />
                Connected
                {connectionId && (
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    style={{
                      marginLeft: '8px',
                      padding: '4px 10px',
                      fontSize: '11px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      color: '#ef4444',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      borderRadius: '6px',
                      cursor: disconnecting ? 'wait' : 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    {disconnecting ? '…' : 'Disconnect'}
                  </button>
                )}
              </>
            ) : (
              <>
                <Icon name="loading" color="warning" darkMode={darkMode} size={14} />
                Pending
              </>
            )}
          </span>
        )}
      </div>

      {/* Inline Booking Form - BookingWizard style */}
      {showBookingForm && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.08) 0%, rgba(61, 90, 32, 0.02) 100%)',
          border: '1px solid rgba(61, 90, 32, 0.25)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#4A8C18', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="calendar" color="brand" darkMode={darkMode} size={20} />
            Book Appointment: {company.name}
          </h3>
          
          {error && <div className="error" style={{ marginBottom: '16px' }}>{error}</div>}
          {success && <div className="success" style={{ marginBottom: '16px' }}>{success}</div>}
          
          <form onSubmit={handleBooking}>
            {/* Step 1: Bike Selection - Card style like BookingWizard */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', color: 'rgba(255,255,255,0.9)', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="bike" color="primary" darkMode={darkMode} size={18} />
                Select Your Bike
              </label>
              {bikes.length === 0 ? (
                <div style={{ 
                  padding: '20px', 
                  background: 'var(--bg-overlay-light)', 
                  borderRadius: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  <Icon name="bike" color="muted" darkMode={darkMode} size={32} style={{ marginBottom: '8px' }} />
                  You have no registered bikes. Please add a bike to your profile first!
                  <Link to="/profile" className="btn btn-primary" style={{ display: 'inline-block', marginTop: '12px' }}>
                    + Add Bike
                  </Link>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {bikes.map(bike => (
                    <div
                      key={bike.id}
                      onClick={() => setSelectedBikeId(String(bike.id))}
                      style={{
                        padding: '16px',
                        background: selectedBikeId === String(bike.id) 
                          ? 'linear-gradient(135deg, rgba(61, 90, 32, 0.3) 0%, rgba(160, 200, 20, 0.2) 100%)'
                          : 'var(--bg-overlay-light)',
                        border: selectedBikeId === String(bike.id)
                          ? '2px solid #4A8C18'
                          : '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      <Icon name="bike" color="primary" darkMode={darkMode} size={24} />
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#ffffff', fontWeight: '600', fontSize: '15px' }}>
                          {bike.brand} {bike.model}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                          {bike.year} • {bike.vin}
                        </div>
                      </div>
                      {selectedBikeId === String(bike.id) && (
                        <Icon name="success" color="success" darkMode={darkMode} size={20} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Step 2: Service Selection - Card style */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', color: 'rgba(255,255,255,0.9)', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="service" color="primary" darkMode={darkMode} size={18} />
                Select Service Type
              </label>
              {templates.length === 0 ? (
                <div style={{ 
                  padding: '20px', 
                  background: 'var(--bg-overlay-light)', 
                  borderRadius: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  No service packages available at this shop.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {templates.map(template => (
                    <div
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplateId(String(template.id));
                        setSelectedSlot(null);
                        setSelectedDate('');
                      }}
                      style={{
                        padding: '16px',
                        background: selectedTemplateId === String(template.id)
                          ? 'linear-gradient(135deg, rgba(61, 90, 32, 0.3) 0%, rgba(160, 200, 20, 0.2) 100%)'
                          : 'var(--bg-overlay-light)',
                        border: selectedTemplateId === String(template.id)
                          ? '2px solid #4A8C18'
                          : '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#ffffff', fontWeight: '600', fontSize: '15px' }}>
                          {template.name}
                        </div>
                        {template.description && (
                          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '4px' }}>
                            {template.description}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                          <Icon name="schedule" color="secondary" darkMode={darkMode} size={14} />
                          {template.estimated_duration_minutes} min
                        </div>
                        {template.default_price && (
                          <div style={{ color: '#4A8C18', fontWeight: '600', fontSize: '14px' }}>
                            £{Number(template.default_price).toFixed(2)}
                          </div>
                        )}
                      </div>
                      {selectedTemplateId === String(template.id) && (
                        <Icon name="success" color="success" darkMode={darkMode} size={20} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Step 3: Calendar for Available Slots */}
            {selectedTemplateId && selectedBikeId && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '12px', color: 'rgba(255,255,255,0.9)', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="calendar" color="primary" darkMode={darkMode} size={18} />
                  Select Date and Time
                </label>
                <AvailableSlotsCalendar
                  companyId={companyId}
                  serviceTemplateId={selectedTemplateId}
                  selectedDate={selectedDate}
                  onDateSelect={handleCalendarDateSelect}
                  onSlotSelect={handleCalendarSlotSelect}
                />
              </div>
            )}

            {(!selectedTemplateId || !selectedBikeId) && (
              <div style={{
                padding: '24px',
                background: 'var(--bg-overlay-light)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '12px',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.5)',
                marginBottom: '24px'
              }}>
                <Icon name="calendar" color="muted" darkMode={darkMode} size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                {!selectedBikeId ? 'Please select a bike first' : 'Select a service type to view available times'}
              </div>
            )}

            {/* Notes */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon name="write" color="secondary" darkMode={darkMode} size={14} />
                Notes (optional)
              </label>
              <textarea
                className="form-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="3"
                placeholder="Any special requests or notes about your bike..."
                style={{ width: '100%', resize: 'vertical', color: '#ffffff' }}
              />
            </div>

            {/* Selected slot summary */}
            {selectedSlot && (
              <div style={{
                padding: '16px',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                marginBottom: '24px'
              }}>
                <div style={{ color: '#1A2811', fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="success" color="success" darkMode={darkMode} size={16} />
                  Selected Time Slot
                </div>
                <div style={{ color: '#ffffff' }}>
                  {selectedDate} • {selectedSlot.start} - {selectedSlot.end}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={!selectedSlot || !selectedBikeId || !selectedTemplateId || bookingLoading}
                style={{ flex: 1, minWidth: '200px', padding: '14px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
              >
                {bookingLoading ? (
                  <>
                    <Icon name="loading" color="primary" darkMode={darkMode} size={18} />
                    Booking...
                  </>
                ) : (
                  <>
                    <Icon name="success" color="primary" darkMode={darkMode} size={18} />
                    Confirm Booking
                  </>
                )}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowBookingForm(false)}
                style={{ padding: '14px 24px' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Services */}
      <div style={{
        background: 'var(--bg-overlay-light)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#ffffff', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="service" color="primary" darkMode={darkMode} size={20} />
          Services
        </h2>
        {templates.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.5)' }}>No published services.</p>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {templates.map(t => (
              <div key={t.id} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', color: '#ffffff' }}>{t.name}</h4>
                  {t.description && (
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{t.description}</p>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#ffffff', fontWeight: '600' }}>
                    {t.estimated_duration_minutes} min
                  </div>
                  {t.default_price && (
                    <div style={{ color: '#4A8C18', fontSize: '14px', marginTop: '2px' }}>
                      £{Number(t.default_price).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reviews - Google Reviews Style */}
      <div style={{
        background: 'var(--bg-overlay-light)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#ffffff', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="star" color="primary" darkMode={darkMode} size={20} />
            Customer Reviews
          </h2>
          {reviews.length > 0 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '8px 16px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '20px'
            }}>
              <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#fbbf24' }}>
                {(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)}
              </span>
              <Icon name="star" color="warning" darkMode={darkMode} size={20} />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                ({reviews.length} reviews)
              </span>
            </div>
          )}
        </div>
        
        {reviews.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: 'rgba(255,255,255,0.5)'
          }}>
            <Icon name="star" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No reviews yet. Be the first to review!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {reviews.map(review => (
              <div key={review.id} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: '12px',
                padding: '20px',
                transition: 'all 0.2s'
              }}>
                {/* Header with avatar and name */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                  {/* Avatar */}
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1A2811 0%, #4A8C18 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1a1a2e',
                    flexShrink: 0
                  }}>
                    {(review.user_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Name and rating */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <div style={{ 
                          fontWeight: '600', 
                          color: '#ffffff', 
                          fontSize: '15px',
                          marginBottom: '4px'
                        }}>
                          {review.user_name || 'Anonymous User'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ display: 'flex', gap: '3px' }}>
                            {[1, 2, 3, 4, 5].map(star => (
                              <span 
                                key={star} 
                                style={{ 
                                  color: star <= review.rating ? '#fbbf24' : 'rgba(255,255,255,0.2)',
                                  fontSize: '22px'
                                }}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                            {new Date(review.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                      </div>
                      {review.is_verified && (
                        <span style={{
                          padding: '4px 8px',
                          background: 'rgba(26, 40, 17, 0.15)',
                          color: '#1A2811',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Icon name="verified" color="success" darkMode={darkMode} size={12} />
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Service and Bike Info */}
                {(review.service || review.bike) && (
                  <div style={{
                    marginBottom: '12px',
                    paddingLeft: '56px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.5)'
                  }}>
                    {review.bike && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: 'rgba(61, 90, 32, 0.1)',
                        borderRadius: '6px',
                        border: '1px solid rgba(61, 90, 32, 0.2)'
                      }}>
                        <Icon name="bike" color="brand" darkMode={darkMode} size={14} />
                        <span>
                          {review.bike.brand} {review.bike.model}
                          {review.bike.year && ` (${review.bike.year})`}
                        </span>
                      </div>
                    )}
                    {review.service && review.service.service_date && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: 'rgba(59, 130, 246, 0.1)',
                        borderRadius: '6px',
                        border: '1px solid rgba(59, 130, 246, 0.2)'
                      }}>
                        <Icon name="service" color="info" darkMode={darkMode} size={14} />
                        <span>
                          {new Date(review.service.service_date).toLocaleDateString('en-GB')}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Comment */}
                {review.comment && (
                  <p style={{ 
                    margin: 0, 
                    color: 'rgba(255,255,255,0.8)', 
                    fontSize: '14px',
                    lineHeight: '1.6',
                    paddingLeft: '56px'
                  }}>
                    {review.comment}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default CompanyPublicProfilePage;
